<template>
  <AppLayout pageTitle="Plots">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card border-0 overflow-hidden" style="border-radius: 16px; background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #fcd34d 100%);">
          <div class="card-body py-4 px-4">
            <div class="d-flex align-items-center">
              <div class="rounded-3 d-flex align-items-center justify-content-center me-3" style="width: 52px; height: 52px; background: rgba(255,255,255,0.25);">
                <i class="ph-duotone ph-map-pin-line text-white f-s-24"></i>
              </div>
              <div>
                <h4 class="text-white fw-bold mb-1">Available Plots</h4>
                <p class="text-white-50 mb-0 f-s-14">Browse plots by project, view details and express interest</p>
              </div>
              <div class="ms-auto d-none d-sm-block">
                <span class="badge bg-white bg-opacity-25 text-white px-3 py-2 rounded-pill fw-semibold">
                  {{ filteredPlots.length }} of {{ plots.length }} Plots
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Search + Filter Bar -->
    <div class="row mb-3" v-if="!isLoading">
      <div class="col-12">
        <div class="card border-0" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div class="card-body py-3 px-4">
            <!-- Search -->
            <div class="d-flex flex-column flex-md-row gap-3 align-items-md-center">
              <div class="flex-grow-1" style="max-width: 480px;">
                <div class="input-group">
                  <span class="input-group-text bg-white border-end-0" style="border-radius: 10px 0 0 10px; border-color: #e2e8f0;">
                    <i class="ph-duotone ph-magnifying-glass text-muted"></i>
                  </span>
                  <input type="text" class="form-control border-start-0" v-model="searchQuery"
                         placeholder="Search plot number, type, project..."
                         style="border-radius: 0 10px 10px 0; border-color: #e2e8f0; padding: 10px 14px;">
                </div>
              </div>
              <div class="d-flex gap-2 flex-wrap">
                <button class="btn btn-sm fw-semibold"
                        :style="statusFilter === 'available' ? 'background: #ecfdf5; color: #10b981; border: 1px solid #a7f3d0; border-radius: 10px; padding: 8px 14px;' : 'background: #f8fafc; color: #64748b; border: 1px solid #e2e8f0; border-radius: 10px; padding: 8px 14px;'"
                        @click="statusFilter = statusFilter === 'available' ? '' : 'available'">
                  <i class="ph-bold ph-check-circle me-1"></i> Available
                </button>
                <button class="btn btn-sm fw-semibold"
                        :style="statusFilter === 'sold' ? 'background: #fef2f2; color: #ef4444; border: 1px solid #fecaca; border-radius: 10px; padding: 8px 14px;' : 'background: #f8fafc; color: #64748b; border: 1px solid #e2e8f0; border-radius: 10px; padding: 8px 14px;'"
                        @click="statusFilter = statusFilter === 'sold' ? '' : 'sold'">
                  <i class="ph-bold ph-x-circle me-1"></i> Sold
                </button>
              </div>
            </div>

            <!-- Project Filter Chips -->
            <div v-if="projects.length > 0" class="d-flex flex-wrap gap-2 mt-3 pt-3 border-top" style="border-color: #f1f5f9 !important;">
              <button class="btn btn-sm fw-semibold"
                      :style="!selectedProjectId ? 'background: linear-gradient(135deg, #f59e0b, #fbbf24); color: white; border-radius: 20px; padding: 6px 16px;' : 'background: #f8fafc; color: #64748b; border-radius: 20px; padding: 6px 16px; border: 1px solid #e2e8f0;'"
                      @click="filterByProject(null)">
                All Projects
              </button>
              <button v-for="proj in projects" :key="proj.id" class="btn btn-sm fw-semibold"
                      :style="selectedProjectId === proj.id ? 'background: linear-gradient(135deg, #f59e0b, #fbbf24); color: white; border-radius: 20px; padding: 6px 16px;' : 'background: #f8fafc; color: #64748b; border-radius: 20px; padding: 6px 16px; border: 1px solid #e2e8f0;'"
                      @click="filterByProject(proj.id)">
                {{ proj.name }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Plots Grid -->
    <div class="row" v-if="!isLoading">
      <div class="col-xl-4 col-lg-6 col-sm-6 mb-4" v-for="plot in paginatedPlots" :key="plot.id">
        <div class="card border-0 h-100 position-relative" 
             style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06); transition: transform 0.2s, box-shadow 0.2s; overflow: hidden;"
             @mouseover="$event.currentTarget.style.transform='translateY(-4px)'; $event.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.12)'"
             @mouseout="$event.currentTarget.style.transform='translateY(0)'; $event.currentTarget.style.boxShadow='0 2px 12px rgba(0,0,0,0.06)'">
          <!-- Status stripe -->
          <div style="height: 4px;"
               :style="'background:' + (plot.status === 'available' ? 'linear-gradient(90deg, #10b981, #34d399)' : plot.status === 'sold' ? 'linear-gradient(90deg, #ef4444, #f87171)' : 'linear-gradient(90deg, #f59e0b, #fbbf24)')">
          </div>
          <div class="card-body p-4">
            <div class="d-flex align-items-start justify-content-between mb-3">
              <div>
                <h5 class="fw-bold mb-1" style="color: #0f172a; font-size: 1.15rem;">Plot {{ plot.plot_no }}</h5>
                <p v-if="plot.project" class="text-muted f-s-12 mb-0">
                  <i class="ph-duotone ph-buildings me-1"></i> {{ plot.project.name }}
                </p>
              </div>
              <span class="badge rounded-pill px-3 py-2 f-s-11 fw-semibold"
                    :style="plot.status === 'available' ? 'background:#ecfdf5; color:#10b981;' : plot.status === 'sold' ? 'background:#fef2f2; color:#ef4444;' : 'background:#fffbeb; color:#f59e0b;'">
                <i :class="plot.status === 'available' ? 'ph-bold ph-check-circle' : plot.status === 'sold' ? 'ph-bold ph-x-circle' : 'ph-bold ph-clock'" class="me-1"></i>
                {{ plot.status }}
              </span>
            </div>

            <!-- Plot Details -->
            <div class="row g-2 mb-3">
              <div class="col-6">
                <div class="p-2 rounded-3 text-center" style="background: #f8fafc;">
                  <small class="d-block text-muted f-s-10 fw-medium text-uppercase" style="letter-spacing: 0.5px;">Size</small>
                  <span class="fw-bold f-s-14" style="color: #0f172a;"><i class="ph-duotone ph-ruler me-1 text-primary"></i> {{ plot.size }} sqm</span>
                </div>
              </div>
              <div class="col-6">
                <div class="p-2 rounded-3 text-center" style="background: #f8fafc;">
                  <small class="d-block text-muted f-s-10 fw-medium text-uppercase" style="letter-spacing: 0.5px;">Type</small>
                  <span class="fw-bold f-s-14" :style="'color:' + (plot.plot_type === 'commercial' ? '#0891b2' : '#10b981')">
                    {{ plot.plot_type || 'Residential' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Demarcation badge -->
            <div v-if="plot.coordinates" class="mb-3 p-2 rounded-3 d-flex align-items-center" style="background: #fffbeb; border: 1px dashed #fbbf24;">
              <i class="ph-duotone ph-polygon me-2" style="color: #92400e;"></i>
              <span class="f-s-11 fw-semibold" style="color: #92400e;">Boundary demarcated</span>
              <span class="ms-auto text-muted f-s-10">{{ getCoordCount(plot.coordinates) }} pts</span>
            </div>

            <!-- Price + Action -->
            <div class="pt-3 border-top d-flex align-items-center justify-content-between">
              <div>
                <small class="d-block text-muted f-s-10 fw-medium text-uppercase mb-1" style="letter-spacing: 0.5px;">Price</small>
                <p class="mb-0 fw-bold" :class="store.balancesHidden ? 'balance-blurred' : 'balance-visible'" style="color: #f59e0b; font-size: 1.15rem;">
                  KES {{ Number(plot.price).toLocaleString() }}
                </p>
              </div>
              <button v-if="plot.status === 'available'" class="btn btn-sm fw-semibold"
                      @click="showInterestModal(plot)"
                      style="background: linear-gradient(135deg, #0891b2, #06b6d4); color: white; border-radius: 10px; padding: 8px 18px; font-size: 12px;">
                <i class="ph-duotone ph-hand-waving me-1"></i> Express Interest
              </button>
              <span v-else-if="plot.status === 'sold'" class="fw-semibold f-s-12 text-danger">
                <i class="ph-bold ph-x-circle me-1"></i> Sold
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- No results -->
      <div v-if="filteredPlots.length === 0" class="col-12">
        <div class="card border-0 text-center py-5" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div class="card-body">
            <i class="ph-duotone ph-map-pin-line f-s-30 text-muted"></i>
            <p class="text-muted mt-2 mb-0">{{ searchQuery ? 'No plots match your search' : selectedProjectId ? 'No plots found for this project' : 'No plots available' }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="!isLoading && totalPages > 1" class="row mb-4">
      <div class="col-12">
        <div class="card border-0" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div class="card-body py-3 px-4 d-flex justify-content-between align-items-center">
            <span class="text-muted f-s-13">
              Showing {{ (currentPage - 1) * perPage + 1 }}–{{ Math.min(currentPage * perPage, filteredPlots.length) }} of {{ filteredPlots.length }} plots
            </span>
            <div class="d-flex align-items-center gap-1">
                <button class="btn btn-sm border-0 rounded-2 fw-medium" :disabled="currentPage === 1" @click="currentPage > 1 && currentPage--"
                        style="color: #f59e0b; background: transparent; min-width: 32px; height: 32px; padding: 0;">
                  <i class="ph-bold ph-caret-left"></i>
                </button>
                <template v-for="page in displayPages" :key="page">
                  <button v-if="page === '...'" class="btn btn-sm border-0 rounded-2 f-s-12 text-muted" disabled
                          style="background: transparent; min-width: 32px; height: 32px; padding: 0;">...</button>
                  <button v-else class="btn btn-sm border-0 rounded-2 fw-semibold f-s-12" @click="currentPage = page"
                          :style="page === currentPage ? 'background: linear-gradient(135deg, #f59e0b, #fbbf24); color: white; min-width: 32px; height: 32px; padding: 0;' : 'color: #64748b; background: transparent; min-width: 32px; height: 32px; padding: 0;'">
                    {{ page }}
                  </button>
                </template>
                <button class="btn btn-sm border-0 rounded-2 fw-medium" :disabled="currentPage === totalPages" @click="currentPage < totalPages && currentPage++"
                        style="color: #f59e0b; background: transparent; min-width: 32px; height: 32px; padding: 0;">
                  <i class="ph-bold ph-caret-right"></i>
                </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div class="row" v-if="isLoading">
      <div class="col-xl-4 col-lg-6 col-sm-6 mb-4" v-for="n in 6" :key="n">
        <div class="card border-0" style="border-radius: 14px;">
          <div style="height: 4px; background: #e2e8f0;"></div>
          <div class="card-body p-4 placeholder-glow">
            <span class="placeholder col-5 rounded mb-2" style="height: 20px;"></span>
            <span class="placeholder col-3 rounded mb-3"></span>
            <div class="row g-2 mb-3">
              <div class="col-6"><span class="placeholder col-12 rounded" style="height: 50px;"></span></div>
              <div class="col-6"><span class="placeholder col-12 rounded" style="height: 50px;"></span></div>
            </div>
            <span class="placeholder col-7 rounded" style="height: 24px;"></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Expression of Interest Modal -->
    <div v-if="showEoiModal" class="modal d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);" @click.self="showEoiModal = false">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0" style="border-radius: 16px; overflow: hidden;">
          <div style="height: 4px; background: linear-gradient(90deg, #0891b2, #06b6d4, #22d3ee);"></div>
          <div class="modal-header border-0 pb-0 px-4 pt-4">
            <h5 class="fw-bold" style="color: #0f172a;">
              <i class="ph-duotone ph-hand-waving me-2 text-primary"></i> Express Interest
            </h5>
            <button type="button" class="btn-close" @click="showEoiModal = false"></button>
          </div>
          <div class="modal-body px-4 pb-4">
            <div class="p-3 rounded-3 mb-3" style="background: #ecfeff;">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <span class="fw-bold f-s-14" style="color: #0f172a;">
                  <i class="ph-duotone ph-map-pin-line me-1 text-primary"></i> Plot {{ eoiPlot?.plot_no }}
                </span>
                <span class="badge rounded-pill bg-light text-dark f-s-11">{{ eoiPlot?.size }} sqm</span>
              </div>
              <p class="mb-0 fw-bold f-s-16" style="color: #f59e0b;">
                KES {{ eoiPlot?.price ? Number(eoiPlot.price).toLocaleString() : '—' }}
              </p>
              <p v-if="eoiPlot?.project" class="mb-0 f-s-12 mt-1 text-muted">
                <i class="ph-duotone ph-buildings me-1"></i> {{ eoiPlot.project.name }}
              </p>
            </div>

            <div v-if="eoiSuccess" class="alert border-0 mb-3" style="background: #ecfdf5; color: #10b981; border-radius: 12px;">
              <i class="ph-bold ph-check-circle me-2"></i> {{ eoiSuccess }}
            </div>
            <div v-if="eoiError" class="alert border-0 mb-3" style="background: #fef2f2; color: #ef4444; border-radius: 12px;">
              <i class="ph-bold ph-warning-circle me-2"></i> {{ eoiError }}
            </div>

            <p class="text-muted f-s-13 mb-3">
              By submitting, your contact details will be shared with Sheria Housing for follow-up regarding this plot.
            </p>

            <button class="btn w-100 fw-semibold" @click="submitInterest" :disabled="eoiLoading"
                    style="background: linear-gradient(135deg, #0891b2, #06b6d4); color: white; border-radius: 10px; padding: 12px;">
              <span v-if="eoiLoading" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="ph-duotone ph-paper-plane-tilt me-2"></i>
              {{ eoiLoading ? 'Submitting...' : 'Confirm Interest' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue';
import api from '@/services/axios.js';
import { useUserStore } from '@/store';

export default {
  name: 'PlotsPage',
  components: { AppLayout },
  data() {
    return {
      plots: [],
      projects: [],
      isLoading: true,
      selectedProjectId: null,
      statusFilter: '',
      searchQuery: '',
      currentPage: 1,
      perPage: 12,
      showEoiModal: false,
      eoiPlot: null,
      eoiLoading: false,
      eoiSuccess: '',
      eoiError: '',
    };
  },
  computed: {
    store() {
      return useUserStore();
    },
    filteredPlots() {
      let result = this.plots;
      if (this.selectedProjectId) {
        result = result.filter(p => p.project_id === this.selectedProjectId);
      }
      if (this.statusFilter) {
        result = result.filter(p => p.status === this.statusFilter);
      }
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim();
        result = result.filter(p =>
          (p.plot_no && p.plot_no.toLowerCase().includes(q)) ||
          (p.plot_type && p.plot_type.toLowerCase().includes(q)) ||
          (p.status && p.status.toLowerCase().includes(q)) ||
          (p.project && p.project.name && p.project.name.toLowerCase().includes(q)) ||
          (p.size && String(p.size).includes(q))
        );
      }
      return result;
    },
    totalPages() {
      return Math.ceil(this.filteredPlots.length / this.perPage);
    },
    paginatedPlots() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredPlots.slice(start, start + this.perPage);
    },
    displayPages() {
      const total = this.totalPages;
      const current = this.currentPage;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const pages = [];
      pages.push(1);
      if (current > 3) pages.push('...');
      for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
      if (current < total - 2) pages.push('...');
      pages.push(total);
      return pages;
    },
  },
  watch: {
    searchQuery() { this.currentPage = 1; },
    selectedProjectId() { this.currentPage = 1; },
    statusFilter() { this.currentPage = 1; },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        this.isLoading = true;
        const [plotsRes, projectsRes] = await Promise.all([
          api.get('/api/plot-list?portal=true'),
          api.get('/api/project-list?portal=true'),
        ]);
        this.plots = plotsRes.data || [];
        this.projects = projectsRes.data || [];
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        this.isLoading = false;
      }
    },
    filterByProject(projectId) {
      this.selectedProjectId = projectId;
      this.statusFilter = '';
    },
    getCoordCount(coordinates) {
      try {
        const parsed = typeof coordinates === 'string' ? JSON.parse(coordinates) : coordinates;
        if (parsed?.coordinates?.[0]) return parsed.coordinates[0].length;
        if (Array.isArray(parsed)) return parsed.length;
        return 0;
      } catch { return 0; }
    },
    showInterestModal(plot) {
      this.eoiPlot = plot;
      this.eoiSuccess = '';
      this.eoiError = '';
      this.showEoiModal = true;
    },
    async submitInterest() {
      try {
        this.eoiLoading = true;
        this.eoiError = '';
        await api.post('/api/plot-interest', { plot_id: this.eoiPlot.id });
        this.eoiSuccess = 'Your interest has been submitted successfully! Our team will contact you soon.';
        setTimeout(() => { this.showEoiModal = false; }, 3000);
      } catch (error) {
        this.eoiError = error.response?.data?.message || error.response?.data?.error || 'Failed to submit interest. Please try again.';
      } finally {
        this.eoiLoading = false;
      }
    },
  },
};
</script>